import PropTypes from "prop-types";
import React, { Component } from "react";
import TextField from "material-ui/TextField";
import { themeOptions } from "styles/theme";
import { Decorator as FormsyElement } from "formsy-react";
import { extractFormsyProps } from "lib/helpers";
import Globalize from "globalize";
import _ from "lodash";
import Label from "./label";

import Cancel from "material-ui/svg-icons/navigation/cancel";
import { DATATEST } from "constants/integration";

const styles = {
    container: {
        display: "inline-block",
        position: "relative",
    },
    base: {
        borderRadius: "4px",
        textTransform: "none",
    },
    baseLabel: {
        textTransform: "none",
    },
    input: {
        border: "1px solid " + themeOptions.palette.lightGrey,
        padding: "0px 16px",
        height: "40px",
    },
    inputLarge: {
        height: "50px",
    },
    textfield: {
        border: "1px solid " + themeOptions.palette.lightGrey,
        padding: "10px 16px",
        marginBottom: "0px",
    },
    label: {
        color: themeOptions.palette.darkGrey,
        fontWeight: 400,
        top: "2px",
        lineHeight: 1,
        fontSize: "12px",
        transform: "none",
        transformOrigin: "none",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        overflow: "hidden",
        width: "inherit",
        position: "relative",
    },
    description: {
        fontSize: "10px",
        color: themeOptions.palette.darkGrey,
        padding: "4px 0 0",
    },
    descriptionTop: {
        fontSize: "10px",
        color: themeOptions.palette.darkGrey,
    },
    hint: {
        bottom: "4px",
        padding: "16px 0 15px 16px",
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
        overflow: "hidden",
        width: "100%",
    },
    hintLarge: {
        padding: "20px 4px 10px 16px",
        bottom: "4px",
    },
    hintMultiline: {
        padding: "20px 4px 10px 16px",
        bottom: "26px",
    },
    active: {
        border: "1px solid #639CD2",
    },
    activeBorder: {
        border: "1px solid " + themeOptions.palette.mint,
    },
    error: {
        border: "1px solid " + themeOptions.palette.error,
    },
    errorStyle: {
        marginTop: "24px",
        color: themeOptions.palette.error,
    },
    cancel: {
        position: "absolute",
        top: "20px",
        right: "12px",
        transform: "translateY(-50%)",
        zIndex: 2,
        fill: themeOptions.palette.grey,
        transition: "all 0.3s ease",
        opacity: 0,
    },
    cancelShow: {
        opacity: 1,
        cursor: "pointer",
    },
    clear: {
        paddingRight: "40px",
    },
};

/** @augments {Component<any, any>} */
@FormsyElement()
class Field extends Component {
    static propTypes = {
        multiLine: PropTypes.bool,
        errorText: PropTypes.string,
        large: PropTypes.bool,
        maxLength: PropTypes.number,
        label: PropTypes.node,
        labelStyle: PropTypes.object,
        customLabel: PropTypes.node,
        name: PropTypes.string.isRequired, //Formsy elements require this and to be wrapped in a formsy Form tag
        key: PropTypes.string,
        requiredErrorText: PropTypes.string,
        description: PropTypes.string,
        descriptionTop: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
        onChange: PropTypes.func,
        setValue: PropTypes.func, //Reserved function for formsy validations
        allowWhitespace: PropTypes.bool,
        canClear: PropTypes.bool,
        clearStyle: PropTypes.object,
        validateOnInput: PropTypes.bool,
        dataTest: PropTypes.string,
        disabled: PropTypes.bool,
        onEnter: PropTypes.func,
        regex: PropTypes.instanceOf(RegExp),
        debounce: PropTypes.bool,
        activeBorder: PropTypes.bool,
    };

    static defaultProps = {
        primary: false,
        large: true,
        multiLine: false,
        allowWhitespace: true,
        canClear: false,
        disabled: false,
        onEnter: null,
        debounce: false,
    };

    constructor(props) {
        super(props);
        this.state = {
            active: false,
        };
    }

    UNSAFE_componentWillMount() {
        if (this.props.defaultValue) {
            this.props.setValue(this.props.defaultValue);
        }
    }

    handleFocus = () => {
        const { onFocus } = this.props;
        this.setState({
            active: true,
        });
        if (onFocus) {
            onFocus();
        }
    };

    handleBlur = (event) => {
        const { onBlur } = this.props;
        this.setState({
            active: false,
        });
        if (onBlur) {
            onBlur(event);
        }
    };

    sendChange = (event, value) => {
        const { onChange } = this.props;
        onChange?.(event, value, this.refs.field);
    };

    sendChangeDebounced = _.debounce(this.sendChange, 1000);

    handleChangeEvent = (event, value) => {
        const { debounce, setValue, allowWhitespace } = this.props;
        if (!allowWhitespace) {
            value = value.toString().trim();
        }
        setValue(value); // Formsy function that sets form values on input change
        if (debounce) {
            this.sendChangeDebounced(event, value);
        } else {
            this.sendChange(event, value);
        }
    };

    handleKeyPress = (e) => {
        const { onEnter, regex } = this.props;

        if (regex && !regex?.test(e.key)) {
            return e.preventDefault();
        }

        if (e.key === "Enter" && onEnter) {
            onEnter();
        }
    };

    render() {
        // hackey removal of formsy props that should not get passed down the chain//

        let {
            canClear,

            fullWidth,

            multiLine,

            large,

            label,

            labelStyle = {},

            customLabel,

            errorText,

            errorStyle,

            hintText,

            getErrorMessage,

            isPristine,

            showRequired,

            isFormSubmitted,

            requiredErrorText,

            style,

            allowWhitespace,

            labelClassName,

            clearStyle = {},

            description,

            descriptionTop,

            validateOnInput,

            textFieldRef,

            adornment,

            dataTest,

            key,

            maxLength,

            disabled,

            onEnter,

            debounce,

            name,

            descriptionStyle,
            activeBorder,
            ...other
        } = this.props;

        other = extractFormsyProps(other);

        const { active } = this.state;

        if (other.type === "hidden") {
            return <input type={other.type} name={name} value={other.value} />;
        }

        // debounced input is controlled
        const withDebounce = debounce ? { value: this.props.getValue() } : {};

        errorText = errorText || ((isFormSubmitted() || validateOnInput) && getErrorMessage());
        if (!errorText && !isPristine() && showRequired()) {
            errorText = requiredErrorText || Globalize.formatMessage("Field Required");
        }

        let textareaStyle;
        let inputStyle = this.props.inputStyle || {};
        let hintStyle = this.props.hintStyle || {};
        const descriptionStyles = {
            ...styles.description,
            ...this.props.descriptionStyle,
        };
        if (multiLine) {
            textareaStyle = Object.assign(
                {},
                styles.base,
                label && styles.baseLabel,
                styles.textfield,
                active && styles.active,
                active && activeBorder && styles.activeBorder,
                errorText && styles.error,
                inputStyle,
            );
            hintStyle = Object.assign({}, styles.hint, styles.hintMultiline, hintStyle);
        } else {
            inputStyle = Object.assign(
                {},
                styles.base,
                label && styles.baseLabel,
                styles.input,
                large && styles.inputLarge,
                active && styles.active,
                active && activeBorder && styles.activeBorder,
                errorText && styles.error,
                canClear && other.value && styles.clear,
                inputStyle,
            );
            hintStyle = Object.assign({}, styles.hint, large ? styles.hintLarge : {}, hintStyle);
        }

        return (
            <div style={Object.assign({ width: fullWidth ? "100%" : 256 }, styles.container, style)}>
                {customLabel ||
                    (label && (
                        <Label className={this.props.labelClassName} style={labelStyle}>
                            {label}
                        </Label>
                    ))}
                {descriptionTop && typeof descriptionTop === "string" && (
                    <p className="descriptionTop" style={styles.descriptionTop}>
                        {descriptionTop}
                    </p>
                )}
                {descriptionTop && typeof descriptionTop === "object" && descriptionTop}
                <TextField
                    {...other}
                    {...withDebounce}
                    key={key || name}
                    multiLine={multiLine}
                    underlineShow={false}
                    inputStyle={inputStyle}
                    textareaStyle={textareaStyle}
                    errorText={errorText}
                    errorStyle={errorStyle ? Object.assign({}, styles.errorStyle, errorStyle) : styles.errorStyle}
                    hintText={hintText}
                    hintStyle={hintStyle}
                    title={hintText}
                    onFocus={this.handleFocus}
                    onBlur={this.handleBlur}
                    name={name || label}
                    onChange={this.handleChangeEvent}
                    style={{ width: "100%", height: large ? "50px" : "40px" }}
                    ref={textFieldRef || "field"}
                    data-test={dataTest}
                    maxLength={maxLength}
                    disabled={disabled}
                    onKeyPress={this.handleKeyPress}
                />
                {adornment}
                {canClear && (
                    <Cancel
                        data-test={DATATEST.CLEAR_INPUT}
                        onClick={this.handleChangeEvent.bind(this, null, "")}
                        style={Object.assign({}, styles.cancel, clearStyle, other.value && styles.cancelShow)}
                    />
                )}
                {description && !errorText && (
                    <p style={descriptionStyles} data-test={dataTest ? `${dataTest}-description` : null}>
                        {description}
                    </p>
                )}
            </div>
        );
    }
}

export default Field;
